'use client';

import { useTranslations } from 'next-intl';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { useIsMobile } from '@/hooks/useIsMobile';
import openModal from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';
import { cn } from '@/utils/cn';
import DailyBonusGuestOverlay from './components/DailyBonusGuestOverlay';
import DailyBonusInformationDrawer from './components/DailyBonusInformationDrawer';
import DailyBonusTimer from './components/DailyBonusTimer';
import DailyBonusSkeleton from './components/Skeleton';
import { useDailyBonusRewards } from './hooks/useDailyBonusRewards';

const DailyBonusCard = lazy(() => import('./components/DailyBonusCard'));

const DailyBonus = () => {
  const t = useTranslations('daily-bonus');
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const userSession = useAppSelector(state => state.user.userSession);
  const isGuest = !userSession;
  const { cards, isLoading } = useDailyBonusRewards();

  if (!isHydrated) {
    return <DailyBonusSkeleton />;
  }

  if (!isGuest && isLoading) {
    return <DailyBonusSkeleton />;
  }

  const handleOpenModal = () => {
    if (isMobile) {
      setIsOpen(true);
      return;
    }
    openModal('daily-bonus');
  };

  return (
    <Suspense fallback={<DailyBonusSkeleton />}>
      <section className="relative glass-stroke-border bg-fake-glass-effect md:-mx-2 max-md:h-60 overflow-hidden lg:mb-3.5 max-md:mt-4 rounded-2xl shadow-[1px_2px_10px_0_rgba(0,0,0,0.55)]">
        {isGuest && <DailyBonusGuestOverlay />}

        <div className={cn('relative z-1 p-4 md:px-2 md:py-3', isGuest && 'blur-[0.4375rem]')}>
          <div className="flex items-center justify-between mb-4 md:mb-3">
            <h3 className="text-white leading-5 text-sm font-semibold">{t('daily-bonus-reward')}</h3>
            <DailyBonusTimer />
          </div>

          <div className="grid grid-cols-5 gap-x-1.5 gap-y-2.5 md:gap-y-2">
            {cards.map(({ id, ...cardProps }, index) => (
              <DailyBonusCard key={id} index={index} {...cardProps} />
            ))}
          </div>

          <div className="flex justify-center mt-3.5 md:mt-3">
            <button
              type="button"
              className="flex items-center gap-1 text-[0.625rem] leading-4 text-c300 font-medium"
              onClick={handleOpenModal}
            >
              {t('bonus-activations-stack-how-does-it-work')}
              <Icon name="info-payment" className="size-2.5 text-c300" />
            </button>
          </div>
        </div>

        <DailyBonusInformationDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
      </section>
    </Suspense>
  );
};

export default DailyBonus;
